import { useAuthStore } from "../stores/authStore";
import { canRolePerformAction, getRolePermissions, ModuleType } from "../constants/rbac";

/**
 * Enhanced permission hook that checks:
 * 1. Direct permissions from API (if available)
 * 2. Role-based RBAC matrix (fallback)
 */
export const useRBACPermission = (module: ModuleType, action: string): boolean => {
  const user = useAuthStore((state) => state.user);

  // First, check direct permissions from API
  if (user?.permissions !== undefined) {
    // If API returned permissions (even empty array), use strict checking
    if (user.permissions.length === 0) return true; // Empty array = full access
    const hasDirectPermission = user.permissions.some(
      (p) => p.module === module && p.action === action
    );
    if (hasDirectPermission) return true;
    // If API was called but permission not in list, deny (don't fall back to RBAC)
    return false;
  }

  // Fallback to RBAC matrix only if API permissions haven't been loaded yet
  if (user?.roles && user.roles.length > 0) {
    return user.roles.some((role) => canRolePerformAction(role, module, action));
  }

  return false;
};

/**
 * Check if user can perform any of the specified actions
 */
export const useCanPerformAnyAction = (
  checks: [ModuleType, string][]
): boolean => {
  const user = useAuthStore((state) => state.user);

  return checks.some(([module, action]) => {
    // Check direct permissions first
    if (user?.permissions !== undefined) {
      if (user.permissions.length === 0) return true; // Empty = full access
      const hasDirectPermission = user.permissions.some(
        (p) => p.module === module && p.action === action
      );
      if (hasDirectPermission) return true;
      return false; // API was called, permission not found, don't fall back
    }

    // Check RBAC matrix only if permissions haven't been loaded
    if (user?.roles && user.roles.length > 0) {
      return user.roles.some((role) =>
        canRolePerformAction(role, module, action)
      );
    }

    return false;
  });
};

/**
 * Check if user can perform ALL of the specified actions
 */
export const useCanPerformAllActions = (
  checks: [ModuleType, string][]
): boolean => {
  const user = useAuthStore((state) => state.user);

  return checks.every(([module, action]) => {
    // Check direct permissions first
    if (user?.permissions !== undefined) {
      if (user.permissions.length === 0) return true; // Empty = full access
      const hasDirectPermission = user.permissions.some(
        (p) => p.module === module && p.action === action
      );
      if (hasDirectPermission) return true;
      return false; // API was called, permission not found, don't fall back
    }

    // Check RBAC matrix only if permissions haven't been loaded
    if (user?.roles && user.roles.length > 0) {
      return user.roles.some((role) =>
        canRolePerformAction(role, module, action)
      );
    }

    return false;
  });
};

/**
 * Get all permissions for the current user (from API + RBAC matrix)
 */
export const useUserRBACPermissions = () => {
  const user = useAuthStore((state) => state.user);

  if (!user) return {};

  // If we have direct permissions from API, use those
  if (user.permissions && user.permissions.length > 0) {
    return user.permissions.reduce(
      (acc, perm) => {
        if (!acc[perm.module]) {
          acc[perm.module] = [];
        }
        acc[perm.module].push(perm.action);
        return acc;
      },
      {} as Record<string, string[]>
    );
  }

  // Fallback to RBAC matrix based on roles
  if (user.roles && user.roles.length > 0) {
    const rolePermissions = user.roles.map((role) =>
      getRolePermissions(role)
    );

    // Merge all role permissions
    return rolePermissions.reduce(
      (merged, rolePerms) => {
        Object.entries(rolePerms).forEach(([module, actions]) => {
          if (!merged[module]) {
            merged[module] = [];
          }
          actions.forEach((action) => {
            if (!merged[module].includes(action)) {
              merged[module].push(action);
            }
          });
        });
        return merged;
      },
      {} as Record<string, string[]>
    );
  }

  return {};
};

/**
 * Debug RBAC permissions for current user
 */
export const useDebugRBAC = () => {
  const user = useAuthStore((state) => state.user);
  const permissions = useUserRBACPermissions();

  return {
    user: user ? { userId: user.userId, roles: user.roles } : null,
    permissions,
    hasDirectPermissions: user?.permissions ? user.permissions.length > 0 : false,
  };
};
